<template>
    <div class="productos-page">
        <!-- Header -->
        <section class="page-header">
            <div class="container">
                <h1 class="page-title">{{ categoria ? categoria.nombre : 'Productos' }}</h1>
                <p v-if="categoria && categoria.descripcion" class="page-subtitle">
                    {{ categoria.descripcion }}
                </p>
            </div>
        </section>

        <!-- Content -->
        <section class="products-section">
            <div class="container">
                <div class="products-layout">
                    <!-- SIDEBAR -->
                    <aside class="sidebar">
                        <!-- Buscador -->
                        <div class="filter-box">
                            <h3><i class="fas fa-search"></i> Buscar</h3>
                            <input 
                                v-model="filters.search"
                                type="text" 
                                class="search-input" 
                                placeholder="Buscar productos..."
                                @input="applyFilters"
                            >
                        </div>

                        <!-- Marcas -->
                        <div class="filter-box" v-if="marcasDisponibles.length">
                            <h3><i class="fas fa-tag"></i> Marcas</h3>
                            <div class="filter-list">
                                <label 
                                    v-for="marca in marcasDisponibles" 
                                    :key="marca.id"
                                    class="filter-item"
                                >
                                    <input 
                                        type="checkbox" 
                                        :value="marca.id"
                                        v-model="filters.marcas"
                                        @change="applyFilters"
                                    >
                                    <span>{{ marca.nombre }}</span>
                                </label>
                            </div>
                        </div>

                        <!-- Modelos -->
                        <div class="filter-box" v-if="modelosDisponibles.length">
                            <h3><i class="fas fa-cube"></i> Modelos</h3>
                            <div class="filter-list">
                                <label 
                                    v-for="modelo in modelosDisponibles" 
                                    :key="modelo.id"
                                    class="filter-item"
                                >
                                    <input 
                                        type="checkbox" 
                                        :value="modelo.id"
                                        v-model="filters.modelos"
                                        @change="applyFilters"
                                    >
                                    <span>{{ modelo.nombre }}</span>
                                </label>
                            </div>
                        </div>

                        <!-- Limpiar Filtros -->
                        <button 
                            v-if="hasFilters"
                            @click="clearFilters" 
                            class="btn-clear"
                        >
                            <i class="fas fa-times"></i> Limpiar Filtros
                        </button>
                    </aside>

                    <!-- PRODUCTOS -->
                    <main class="products-main">
                        <!-- Info -->
                        <div class="results-info">
                            <p>Mostrando {{ filteredProductos.length }} de {{ allProductos.length }} productos</p>
                        </div>

                        <!-- Grid -->
                        <div v-if="filteredProductos.length && !loading" class="products-grid">
                            <ProductCard 
                                v-for="producto in filteredProductos" 
                                :key="producto.id"
                                :producto="producto"
                            />
                        </div>

                        <!-- Sin resultados -->
                        <div v-else-if="!loading && !filteredProductos.length" class="no-results">
                            <i class="fas fa-box-open"></i>
                            <h3>No se encontraron productos</h3>
                            <p v-if="hasFilters">Intenta cambiar los filtros o la búsqueda</p>
                            <p v-else>No hay productos en esta categoría</p>
                        </div>

                        <!-- Loading -->
                        <div v-if="loading" class="loading">
                            <div class="spinner"></div>
                            <p>Cargando productos...</p>
                        </div>
                    </main>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import ProductCard from '../components/products/ProductCard.vue';
import api from '../services/api';

export default {
    name: 'ProductosPorCategoria',
    components: {
        ProductCard
    },
    data() {
        return {
            categoria: null,
            allProductos: [],
            filteredProductos: [],
            allMarcas: [],
            allModelos: [],
            filters: {
                search: '',
                marcas: [],
                modelos: []
            },
            loading: true
        };
    },
    computed: {
        hasFilters() {
            return this.filters.search !== '' || 
                   this.filters.marcas.length > 0 || 
                   this.filters.modelos.length > 0;
        },
        marcasDisponibles() {
            // Obtener marcas únicas de los productos de esta categoría
            const marcasIds = [...new Set(this.allProductos.map(p => p.marca_id).filter(Boolean))];
            return this.allMarcas.filter(m => marcasIds.includes(m.id));
        },
        modelosDisponibles() {
            // Obtener modelos únicos de los productos de esta categoría
            const modelosIds = [...new Set(this.allProductos.map(p => p.modelo_id).filter(Boolean))];
            return this.allModelos.filter(m => modelosIds.includes(m.id));
        }
    },
    async created() {
        await this.loadAll();
    },
    methods: {
        async loadAll() {
            try {
                this.loading = true;
                const categoriaId = this.$route.params.categoriaId;

                // Cargar categoría
                const catRes = await api.getCategory(categoriaId);
                this.categoria = catRes.data;

                // Cargar productos de la categoría
                const prodRes = await api.getProductsByCategory(categoriaId);
                this.allProductos = prodRes.data || [];
                this.filteredProductos = [...this.allProductos];

                // Cargar todas las marcas
                const marcasRes = await api.getMarcas();
                this.allMarcas = marcasRes.data || [];

                // Cargar todos los modelos
                const modelosRes = await api.getModelos();
                this.allModelos = modelosRes.data || [];

                this.loading = false;
            } catch (error) {
                console.error('Error loading products:', error);
                this.loading = false;
            }
        },
        applyFilters() {
            let filtered = [...this.allProductos];

            // Filtro de búsqueda
            if (this.filters.search) {
                const query = this.filters.search.toLowerCase();
                filtered = filtered.filter(p => {
                    const nombre = (p.nombre || '').toLowerCase();
                    const descripcion = (p.descripcion || '').toLowerCase();
                    const sku = (p.sku || '').toLowerCase();
                    return nombre.includes(query) || 
                           descripcion.includes(query) || 
                           sku.includes(query);
                });
            }

            // Filtro de marcas
            if (this.filters.marcas.length > 0) {
                filtered = filtered.filter(p => 
                    this.filters.marcas.includes(p.marca_id)
                );
            }

            // Filtro de modelos
            if (this.filters.modelos.length > 0) {
                filtered = filtered.filter(p => 
                    this.filters.modelos.includes(p.modelo_id)
                );
            }

            this.filteredProductos = filtered;
        },
        clearFilters() {
            this.filters.search = '';
            this.filters.marcas = [];
            this.filters.modelos = [];
            this.filteredProductos = [...this.allProductos];
        }
    },
    watch: {
        '$route'() {
            this.clearFilters();
            this.loadAll();
        }
    }
};
</script>

<style scoped>
.productos-page {
    background-color: var(--background);
    min-height: 100vh;
    padding-top: 80px;
}

.page-header {
    background: linear-gradient(135deg, var(--primary-dark), var(--primary-blue));
    padding: 60px 2rem;
    position: relative;
}

.page-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 50%, rgba(212, 175, 55, 0.1), transparent 60%);
}

.container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 1;
}

.page-title {
    font-family: 'Playfair Display', serif;
    font-size: 3rem;
    margin-bottom: 1rem;
    color: var(--text-light);
}

.page-subtitle {
    font-size: 1.2rem;
    color: var(--text-gray);
}

.products-section {
    padding: 60px 2rem;
}

.products-layout {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 3rem;
}

/* SIDEBAR */
.sidebar {
    display: flex;
    flex-direction: column;
    gap: 2rem;
}

.filter-box {
    background: var(--card-bg);
    border-radius: 15px;
    padding: 1.5rem;
    border: 1px solid rgba(212, 175, 55, 0.1);
}

.filter-box h3 {
    font-family: 'Playfair Display', serif;
    color: var(--accent-gold);
    margin-bottom: 1rem;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.search-input {
    width: 100%;
    padding: 0.8rem 1rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(212, 175, 55, 0.2);
    border-radius: 10px;
    color: var(--text-light);
    font-size: 0.95rem;
    transition: all 0.3s ease;
}

.search-input:focus {
    outline: none;
    border-color: var(--accent-gold);
    background: rgba(255, 255, 255, 0.08);
}

.filter-list {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    max-height: 300px;
    overflow-y: auto;
}

.filter-item {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    cursor: pointer;
    color: var(--text-gray);
    transition: color 0.3s ease;
}

.filter-item:hover {
    color: var(--text-light);
}

.filter-item input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
}

.btn-clear {
    width: 100%;
    padding: 0.8rem;
    background: rgba(212, 175, 55, 0.1);
    border: 1px solid var(--accent-gold);
    border-radius: 10px;
    color: var(--accent-gold);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
}

.btn-clear:hover {
    background: var(--accent-gold);
    color: var(--primary-dark);
}

/* MAIN */
.products-main {
    display: flex;
    flex-direction: column;
    gap: 2rem;
}

.results-info {
    color: var(--text-gray);
    font-size: 0.95rem;
}

.products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 2rem;
}

.no-results {
    text-align: center;
    padding: 4rem 2rem;
    color: var(--text-gray);
}

.no-results i {
    font-size: 4rem;
    color: rgba(212, 175, 55, 0.3);
    margin-bottom: 1rem;
}

.no-results h3 {
    font-family: 'Playfair Display', serif;
    color: var(--text-light);
    font-size: 1.8rem;
    margin-bottom: 0.5rem;
}

.loading {
    text-align: center;
    padding: 4rem 2rem;
}

.spinner {
    width: 50px;
    height: 50px;
    border: 5px solid rgba(212, 175, 55, 0.1);
    border-top-color: var(--accent-gold);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* RESPONSIVE */
@media (max-width: 968px) {
    .products-layout {
        grid-template-columns: 1fr;
    }
    
    .sidebar {
        order: 2;
    }
    
    .products-main {
        order: 1;
    }
}

@media (max-width: 640px) {
    .page-title {
        font-size: 2rem;
    }
    
    .products-grid {
        grid-template-columns: 1fr;
    }
}
</style>